import {
  faArrowDown,
  faArrowUp,
  faMinus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from '@/styles';
import type {
  ActivationFunnelData,
  ActivationFunnelTrend,
} from '@/services/workersV2API';

type TrendDirection = 'up' | 'down' | 'flat';

const ICONS = {
  up: faArrowUp,
  down: faArrowDown,
  flat: faMinus,
};

export const trendDirection = (delta: number): TrendDirection => {
  if (delta > 0) return 'up';
  if (delta < 0) return 'down';
  return 'flat';
};

export const trendLabel = (delta: number): string => {
  if (delta === 0) return 'no change';
  return `${delta > 0 ? '+' : ''}${delta} pts`;
};

// The page column is `Stack vertical` (align-items: flex-start): without an
// explicit width the grid shrinks to its content, and the loading skeleton
// collapses to a narrow box (GRAVY-10815).
const Wrap = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  width: '100%',
});

const Caption = styled('div', {
  fontSize: '13px',
  color: '#6B6B6B',
});

const Row = styled('div', {
  display: 'grid',
  width: '100%',
  gridTemplateColumns: 'repeat(5, 1fr)',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  overflow: 'hidden',
  '@media (max-width: 1024px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
});

const Card = styled('div', {
  padding: '16px 20px',
  borderRight: '1px solid #DADADA',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  '&:last-child': { borderRight: 'none' },
});

const Top = styled('div', {
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  flexWrap: 'wrap',
});

const Percent = styled('div', {
  fontSize: '18px',
  fontWeight: 700,
  color: '#1E1E1E',
});

const Trend = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  variants: {
    direction: {
      // 4.5:1 on white, as the ticket asks for chips (review R5B-04):
      // #137333 is 5.9:1 (was #1E8E3E, 4.2:1); #6B6B6B is 5.3:1 (was
      // #7A7A7A, 4.3:1, also on the caption and the "/412" counts).
      up: { color: '#137333' },
      down: { color: '#CF040C' },
      flat: { color: '#6B6B6B' },
    },
  },
});

const Bottom = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: '8px',
  fontSize: '13px',
  color: '#3D3D3D',
});

const Fraction = styled('span', {
  fontWeight: 700,
  '& span': { fontWeight: 400, color: '#6B6B6B' },
});

const Skeleton = styled('div', {
  height: '56px',
  margin: '8px 20px',
  borderRadius: '6px',
  backgroundColor: '#F0F0F0',
});

const TrendChip = ({
  trend,
  periodDays,
  testId,
}: {
  trend: ActivationFunnelTrend;
  periodDays: number;
  testId: string;
}) => {
  const direction = trendDirection(trend.delta);
  const label = trendLabel(trend.delta);
  return (
    <Trend
      direction={direction}
      data-testid={testId}
      data-trend={direction}
      aria-label={`${label} compared with ${periodDays} days ago`}
    >
      <FontAwesomeIcon icon={ICONS[direction]} fixedWidth />
      {label}
    </Trend>
  );
};

type Props = {
  data: ActivationFunnelData | null;
  loading: boolean;
  // GRAVY-10975: the Onboarding / BG check / I-9 tabs lead with "Onboarded"
  // (data.onboarding_complete) in place of the All tab's "Signed up" card.
  variant?: 'all' | 'onboarding';
};

const cardsFor = (data: ActivationFunnelData, variant: Props['variant']) => {
  if (variant !== 'onboarding' || !data.onboarding_complete) return data.stages;
  return [
    data.onboarding_complete,
    ...data.stages.filter((s) => s.key !== 'onboarded'),
  ];
};

const FunnelSummary = ({ data, loading, variant = 'all' }: Props) => {
  if (loading || !data) {
    return (
      <Row aria-busy="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} data-testid="funnelCard-skeleton" />
        ))}
      </Row>
    );
  }

  return (
    <Wrap>
      <Caption data-testid="funnelCohort">
        Workers who signed up in the last {data.cohort_days} days. Arrows
        compare with {data.trend_period_days} days ago.
      </Caption>
      <Row role="list" aria-label="Activation funnel">
        {cardsFor(data, variant).map((stage) => (
          <Card
            key={stage.key}
            role="listitem"
            data-testid={`funnelCard-${stage.key}`}
          >
            <Top>
              <Percent>{stage.percentage}%</Percent>
              {stage.trend && (
                <TrendChip
                  trend={stage.trend}
                  periodDays={data.trend_period_days}
                  testId={`funnelTrend-${stage.key}`}
                />
              )}
            </Top>
            <Bottom>
              <span>{stage.label}</span>
              <Fraction>
                {stage.count}
                <span>/{data.total_workers}</span>
              </Fraction>
            </Bottom>
          </Card>
        ))}
      </Row>
    </Wrap>
  );
};

export default FunnelSummary;
